import React, { FC, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { selectIsGenericSport } from 'astra-core/containers/CommonDataProvider'
import {
  getEventListV2,
  getEventsIdsV2,
  getIsEventListFilterUsed,
  getIsNextPageLoading
} from 'astra-core/containers/EventsProvider'
import { EventsApiGetEventsV2Request } from 'astra-core/containers/EventsProvider/types'
import {
  useFilterBySearch,
  useResetFilters
} from 'astra-core/containers/EventsProvider/hooks/list-filters'
import { prepareListItems } from 'astra-core/utils/eventsList'
import { useVirtualScrollActions } from 'astra-core/containers/EventsProvider/hooks/useVirtualScrollActions'

import { useAppSelector } from 'store'
import { LoaderSpinner } from 'shared/ui/LoaderSpinner'
import { ClearResultSearch } from 'shared/ui/ClearResult'
import { EmptyPageFilter } from 'shared/ui/EmptyPageFilter'
import { OutcomesFetchType } from 'entities/outcomes/model/types'
import { VirtualizedEventsListWithAutoSize as VirtualizedEventsList } from 'widgets/VirtualizedEventsList'
import { useFetchBroadcastsAvailability } from 'entities/broadcast'

import { LIVE_EVENTS_LIST_KEY, LIVE_PAGE_SIZE } from '../../model/constants'

const outcomesFetchType: OutcomesFetchType = {
  kind: 'STREAM'
}

export const EventsTable: FC = () => {
  const { sportId } = useParams<{ sportId: string }>()

  const events = useAppSelector((state) =>
    getEventListV2(state, LIVE_EVENTS_LIST_KEY)
  )

  const eventIds = useAppSelector((state) =>
    getEventsIdsV2(state, LIVE_EVENTS_LIST_KEY)
  )

  useFetchBroadcastsAvailability(eventIds)

  const { value: searchText } = useFilterBySearch(LIVE_EVENTS_LIST_KEY)

  const { onResetFilters } = useResetFilters(LIVE_EVENTS_LIST_KEY)

  const isLoading = useAppSelector((state) =>
    getIsNextPageLoading(state, LIVE_EVENTS_LIST_KEY)
  )

  const isLiveFilterUsed = useAppSelector((state) =>
    getIsEventListFilterUsed(state, LIVE_EVENTS_LIST_KEY)
  )

  const showSportHeader = useAppSelector((state) =>
    selectIsGenericSport(state, +sportId)
  )

  const request: EventsApiGetEventsV2Request = useMemo(
    () => ({
      sportIds: [+sportId],
      scheduledToNow: true
    }),
    [sportId]
  )

  const { hasNextPage, isNextPageLoading, loadNextPage, scrollUp } =
    useVirtualScrollActions({
      listKey: LIVE_EVENTS_LIST_KEY,
      request,
      paramsExist: !!sportId
    })

  const { listItems, pageNumbersOfListItems } = useMemo(() => {
    return prepareListItems(events, LIVE_PAGE_SIZE, {
      groups: showSportHeader ? ['sport', 'tournament'] : ['tournament']
    })
  }, [events, showSportHeader])

  if (events.length === 0) {
    if (isLoading) {
      return <LoaderSpinner />
    }

    if (searchText) {
      return <ClearResultSearch />
    }

    if (isLiveFilterUsed) {
      return <EmptyPageFilter onButtonClickResetFilter={onResetFilters} />
    }
    return null
  }

  return (
    <VirtualizedEventsList
      hasNextPage={hasNextPage}
      isNextPageLoading={isNextPageLoading}
      listItems={listItems}
      listKey={LIVE_EVENTS_LIST_KEY}
      loadNextPage={loadNextPage}
      outcomesFetchType={outcomesFetchType}
      pageNumbersOfListItems={pageNumbersOfListItems}
      scrollUp={scrollUp}
    />
  )
}
